import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    // CarAura: Video erst laden, wenn der Bereich in Sichtweite kommt; auf Smartphones
    // und bei Datensparmodus bleibt das Vorschaubild stehen (spart mehrere MB).
    const connection = /** @type {any} */ (navigator).connection;
    const saveData = connection && connection.saveData;
    if (saveData || window.matchMedia('(max-width: 749px)').matches) return;

    if ('IntersectionObserver' in window) {
      this.caObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.caObserver?.disconnect();
            this.#loadVideo();
          }
        },
        { rootMargin: '300px 0px' }
      );
      this.caObserver.observe(this);
    } else {
      this.#loadVideo();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.caObserver?.disconnect();
  }

  #loadVideo() {
    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
